/* eslint-disable react/prop-types */
import React from 'react';
import {
  Stack,
  Text,
  Depths,
  PrimaryButton,
  TextField,
  Spinner,
  SpinnerSize,
  Icon,
} from '@fluentui/react';
import PropTypes from 'prop-types';
import { sumBy } from 'lodash';
import theme from '../../../../theme.style';
import useStagedTxnReIngestionMutation from '../../hooks/useStagedTxReIngestionMutation';

export default function ProductSeedingRecommendation({ data }) {
  const { stagedTransactionsBreakdown, month } = data;
  const productsToSeed = stagedTransactionsBreakdown.productLookupFailed.filter(
    product => !product.productExists,
  );
  const productsToReIngest = stagedTransactionsBreakdown.productLookupFailed.filter(
    product => product.productExists,
  );

  const stagedTransactionsMutation = useStagedTxnReIngestionMutation();

  return (
    <>
      {productsToSeed.length > 0 && (
        <Stack
          style={{
            display: 'inline-flex',
            backgroundColor: 'white',
            boxShadow: Depths.depth4,
            padding: 20,
          }}
          tokens={{ childrenGap: 14 }}
        >
          <Text style={{ fontWeight: 'bold' }}>
            Seed {productsToSeed.length} {productsToSeed.length > 1 ? 'Products' : 'Product'}
          </Text>

          <Text style={{ fontWeight: 'normal' }}>
            There are <strong>{sumBy(productsToSeed, 'totalCount')}</strong> staged transactions
            with an error reason of ProductLookupFailed which means the product does not exist in
            our products collection. We cannot ingest transactions for product guids that don&apos;t
            exist, so you must seed the products first, then come back here and refresh to re-ingest
            these staged transactions. Here is a list of products to re-ingest.
          </Text>

          <TextField
            multiline
            value={productsToSeed.map(product => `"${product.productGuid}"`).join(',\n')}
          />

          <PrimaryButton>
            <a
              style={{ color: 'white', textDecoration: 'none' }}
              href="https://microsoft.sharepoint.com/teams/RoyaltiesBlockchain/_layouts/OneNote.aspx?id=%2Fteams%2FRoyaltiesBlockchain%2FShared%20Documents%2FNotebooks%2FRoyalties%20Blockchain%20OneNote&wd=target%2802%20-%20Live%20Site%20%2B%20DRI%2F00%20-%20Documentation%2FDRI%20Documentation.one%7C3DE1E2AE-D100-4CDF-922B-323BAE7FEFEB%2FProduct%20Seeding%7C5A15C6F1-7F61-4B14-B12E-CB13FDAE84AC%2F%29
              onenote:https://microsoft.sharepoint.com/teams/RoyaltiesBlockchain/Shared%20Documents/Notebooks/Royalties%20Blockchain%20OneNote/02%20-%20Live%20Site%20+%20DRI/00%20-%20Documentation/DRI%20Documentation.one#Product%20Seeding&section-id={3DE1E2AE-D100-4CDF-922B-323BAE7FEFEB}&page-id={5A15C6F1-7F61-4B14-B12E-CB13FDAE84AC}&end"
              target="_blank"
              rel="noopener noreferrer"
            >
              View Product Seeding Docs
            </a>
          </PrimaryButton>
        </Stack>
      )}

      {productsToReIngest.length > 0 &&
        data.contract.approvalStatus.toLowerCase() === 'approved' && (
          <Stack
            style={{
              display: 'inline-flex',
              backgroundColor: 'white',
              boxShadow: Depths.depth4,
              padding: 20,
            }}
            tokens={{ childrenGap: 14 }}
          >
            <Text style={{ fontWeight: 'bold' }}>
              Re-Ingest ProductLookupFailed Staged Transactions
            </Text>

            <Text style={{ fontWeight: 'normal' }}>
              There are <strong>{sumBy(productsToReIngest, 'totalCount')}</strong> staged
              transactions with an error reason of ProductLookupFailed, but have the products in our
              products collection. This means that someone must have seeded these products so we are
              now ready to re-ingest these staged transactions. Click the button below to re-ingest
              these transactions.
            </Text>

            {stagedTransactionsMutation.error && (
              <Text style={{ fontWeight: 'normal', color: theme.palette.accentError }}>
                {stagedTransactionsMutation.error.response?.data?.message ||
                  stagedTransactionsMutation.error.message}
              </Text>
            )}

            {stagedTransactionsMutation.isSuccess && (
              <Text style={{ fontWeight: 'normal', color: theme.palette.accentTeal }}>
                <Icon iconName="StatusCircleCheckmark" />
                {stagedTransactionsMutation.data}
                <br />
                Next step is to wait some time for calc-engine to re-ingest these transactions then
                hit the submit button above. If problems persist, check that service bus is enabled
                and that calc-engine is enabled.
              </Text>
            )}

            <PrimaryButton
              disabled={
                stagedTransactionsMutation.isLoading || stagedTransactionsMutation.isSuccess
              }
              onClick={() =>
                stagedTransactionsMutation.mutate({
                  productIds: productsToReIngest.map(p => p.productGuid),
                  errorReason: 'ProductLookupFailed',
                  month,
                })
              }
            >
              {stagedTransactionsMutation.isLoading ? (
                <Spinner size={SpinnerSize.xSmall} />
              ) : (
                'Re-Ingest StagedTxs'
              )}
            </PrimaryButton>
          </Stack>
      )}
    </>
  );
}

ProductSeedingRecommendation.propTypes = {
  data: PropTypes.shape({}).isRequired,
};
